/**
 * 条件式の入力欄。打ち終わってから `POST /api/idp/policy/check` に聞いて、
 * **保存を押す前に**読めるかどうかを出す(サーバーは 400 にせず `{ok,error}` を返す)。
 *
 * 文法の真実はサーバー側(`server/app/idp/policy_expr.py`)。早見表も
 * `GET /api/idp/policy/atoms` が返すものをそのまま出す。
 */
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Form, Input, Popover, Space, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";

import { usePolicyAtoms, useCheckExpression } from "./policyHooks";

const { Text } = Typography;

/** 原子と演算子の早見表(クリックで開く)。 */
export function AtomHelp() {
  const atoms = usePolicyAtoms();
  const limits = atoms.data?.limits;
  const content = (
    <div style={{ maxWidth: 520 }}>
      <Table
        scroll={{ x: "max-content" }}
        size="small"
        pagination={false}
        rowKey="expr"
        dataSource={atoms.data?.atoms ?? []}
        columns={[
          { title: "書き方", width: 400, ellipsis: true, onCell: (row) => ({ title: String(row.expr ?? "") }), dataIndex: "expr", render: (v: string) => <Text code>{v}</Text> },
          { title: "意味", width: 320, ellipsis: true, dataIndex: "note" },
        ]}
      />
      <div style={{ marginTop: 8 }}>
        <Space size={[4, 4]} wrap>
          つなぎ方:
          {(atoms.data?.operators ?? []).map((op) => (
            <Tag key={op}>{op}</Tag>
          ))}
        </Space>
        <div>
          <Text type="secondary">
            強さは not &gt; and &gt; or。括弧で明示できる。
            {limits
              ? `全体 ${limits.expressionMax} 文字・1 語 ${limits.tokenMax} 文字・${limits.tokenCountMax} 語・入れ子 ${limits.depthMax} 段まで(not も 1 段)。`
              : ""}
          </Text>
        </div>
        <div>
          <Text type="secondary">
            名前に空白が入る IP グループやサービスは <Text code>ip:&quot;社内 NW&quot;</Text> のように
            引用符で囲む。
          </Text>
        </div>
      </div>
    </div>
  );
  return (
    <Popover content={content} title="条件式の書き方" trigger="click" placement="bottomLeft">
      <a>
        <QuestionCircleOutlined /> 書き方
      </a>
    </Popover>
  );
}

export function ExprField({
  name,
  label,
  extra,
  disabled,
  value,
}: {
  name: string;
  label: string;
  extra?: string;
  disabled?: boolean;
  /** 打ち終わったら検証する値(Form.useWatch で渡す)。 */
  value: string;
}) {
  const check = useCheckExpression();
  const [error, setError] = useState("");

  useEffect(() => {
    const expr = (value ?? "").trim();
    if (!expr) {
      setError("");
      return;
    }
    const timer = window.setTimeout(() => {
      check
        .mutateAsync(expr)
        .then((out) => setError(out.ok ? "" : out.error))
        // 検証そのものが失敗したときは黙る(保存時にサーバーが 400 で教える)
        .catch(() => setError(""));
    }, 400);
    return () => window.clearTimeout(timer);
    // check は毎回新しい参照になるので依存に入れない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Form.Item
      name={name}
      label={
        <Space>
          {label}
          <AtomHelp />
        </Space>
      }
      extra={extra}
      validateStatus={error ? "error" : undefined}
      help={error || undefined}
      rules={[{ required: true, message: `${label}は必須` }]}
    >
      <Input disabled={disabled} maxLength={512} placeholder="always" />
    </Form.Item>
  );
}
